// src/pages/AdminDashboard.jsx
import { useRole } from "../contexts/RoleContext";
import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import Header from "../components/Layout/Header";
import Sidebar from "../components/Layout/Sidebar";
import Dashboard from "../components/Dashboard/Dashboard";
import RiceVarietiesManagement from "../components/RiceVarietiesManagement/RiceVarietiesManagement";
import PestManagement from "../components/PestManagement/PestManagement";
import DiseaseManagement from "../components/DiseaseManagement/DiseaseManagement";
import AdminManagement from "../components/AdminManagement/AdminManagement";
import History from "../components/HistoryManagement/History";
import FileMaintenance from "../components/FileMaintenanceManagement/FileMaintenance";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";

const AdminDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { userRole, loading, isSystemAdmin } = useRole();

  useEffect(() => {
    const logout = () => {
      // mark reason for AdminLogin modal
      localStorage.setItem("auto_logout_reason", "inactivity");
      localStorage.removeItem("admin_token");
      localStorage.removeItem("session_id");
      navigate("/login");
    };

    let inactivityTimer = setTimeout(logout, 10 * 60 * 1000);
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(logout, 10 * 60 * 1000);
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    window.addEventListener("scroll", resetTimer);

    return () => {
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      window.removeEventListener("scroll", resetTimer);
      clearTimeout(inactivityTimer);
    };
  }, [navigate]);

  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem("admin_token");
      const sessionId = localStorage.getItem("session_id");
      if (!token || !sessionId) {
        navigate("/login");
        return;
      }

      const userDoc = await getDoc(doc(db, "accounts", token));
      if (!userDoc.exists() || userDoc.data().currentSession !== sessionId) {
        // mark reason for AdminLogin modal
        localStorage.setItem("auto_logout_reason", "session_ended");
        localStorage.removeItem("admin_token");
        localStorage.removeItem("session_id");
        navigate("/login");
      }
    };

    const interval = setInterval(checkSession, 5000);
    return () => clearInterval(interval);
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!userRole) {
    navigate("/login");
    return null;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar
        onNavigate={(path) => {
          navigate(`/dashboard/${path}`);
          setIsSidebarOpen(false);
        }}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />

      <div className="flex flex-col flex-1 overflow-hidden">
        <Header setIsSidebarOpen={setIsSidebarOpen} />
        <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="rice_varieties" element={<RiceVarietiesManagement />} />
            <Route path="rice_pests" element={<PestManagement />} />
            <Route path="rice_diseases" element={<DiseaseManagement />} />
            <Route
              path="accounts"
              element={isSystemAdmin() ? <AdminManagement /> : <Navigate to="/dashboard" replace />}
            />
            <Route path="history_logs" element={<History />} />
            <Route
              path="file_maintenance"
              element={isSystemAdmin() ? <FileMaintenance /> : <Navigate to="/dashboard" replace />}
            />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;